import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  Webhook, PlugZap, RefreshCw, RotateCcw, Settings2, CheckCircle2, XCircle, Clock, DownloadCloud,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/Dialog';

type DeliveryStatus = 'pending' | 'sent' | 'failed';
type LeadType = 'contact' | 'newsletter' | 'form' | 'ai_chat' | 'test';

interface LeadDelivery {
  id: string;
  lead_type: LeadType;
  lead_id: string | null;
  event: string;
  status: DeliveryStatus;
  attempts: number;
  response_code: number | null;
  endpoint: string | null;
  error: string | null;
  payload: Record<string, unknown>;
  delivered_at: string | null;
  created_at: string;
}

interface Paginated<T> { data: T[]; total: number; current_page: number; last_page: number; }
type AllSettings = Record<string, Record<string, string | null>>;

const STATUS: Record<DeliveryStatus, { label: string; variant: 'success' | 'warning' | 'destructive'; icon: typeof Clock }> = {
  pending: { label: 'Pendiente', variant: 'warning', icon: Clock },
  sent: { label: 'Enviado', variant: 'success', icon: CheckCircle2 },
  failed: { label: 'Fallido', variant: 'destructive', icon: XCircle },
};

const TYPE_LABEL: Record<LeadType, string> = {
  contact: 'Contacto',
  newsletter: 'Newsletter',
  form: 'Formulario',
  ai_chat: 'Chatbot IA',
  test: 'Prueba',
};

export const Route = createFileRoute('/_app/integrations')({
  component: IntegrationsPage,
});

function IntegrationsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<DeliveryStatus | ''>('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<LeadDelivery | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => unwrap<AllSettings>(api.get('/settings')),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['lead-deliveries', status, page],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '25', page: String(page) });
      if (status) params.set('status', status);
      return unwrap<Paginated<LeadDelivery>>(api.get(`/integrations/crm/deliveries?${params}`));
    },
    refetchInterval: 15_000,
  });

  const integrations = settings?.integrations ?? {};
  const enabled = integrations.crm_enabled === 'true' || integrations.crm_enabled === '1';
  const endpoint = (integrations.crm_endpoint ?? '').trim();

  const test = useMutation({
    mutationFn: () => api.post('/integrations/crm/test', {}),
    onSuccess: (res) => {
      toast.success(res.data?.message ?? 'Conexión correcta');
      void qc.invalidateQueries({ queryKey: ['lead-deliveries'] });
    },
    onError: (e: AxiosError<{ message?: string }>) =>
      toast.error(e.response?.data?.message ?? 'No se pudo conectar con el CRM'),
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/integrations/crm/deliveries/${id}/retry`, {}),
    onSuccess: () => {
      toast.success('Reintento encolado');
      void qc.invalidateQueries({ queryKey: ['lead-deliveries'] });
    },
    onError: () => toast.error('No se pudo reintentar'),
  });

  const resync = useMutation({
    mutationFn: () => api.post('/integrations/crm/resync', {}),
    onSuccess: (res) => {
      toast.success(res.data?.message ?? 'Re-sincronización encolada');
      void qc.invalidateQueries({ queryKey: ['lead-deliveries'] });
    },
    onError: (e: AxiosError<{ message?: string }>) =>
      toast.error(e.response?.data?.message ?? 'No se pudo re-sincronizar'),
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Webhook className="size-7 text-neutral-700" /> Integraciones · CRM
          </h1>
          <p className="text-sm text-neutral-500">
            Estado del reenvío de leads a tu CRM externo y registro de cada entrega.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/settings"><Settings2 className="size-4" /> Configurar</Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => resync.mutate()}
            disabled={resync.isPending || !enabled || !endpoint}
            title="Reenvía los leads pendientes/fallidos y los que entraron con el CRM desactivado"
          >
            <DownloadCloud className="size-4" /> {resync.isPending ? 'Sincronizando…' : 'Sincronizar pendientes'}
          </Button>
          <Button onClick={() => test.mutate()} disabled={test.isPending || !endpoint}>
            <PlugZap className="size-4" /> {test.isPending ? 'Probando…' : 'Probar conexión'}
          </Button>
        </div>
      </header>

      <StatusBanner enabled={enabled} endpoint={endpoint} />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Entregas</CardTitle>
              <CardDescription>{data?.total ?? 0} envíos registrados.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value as DeliveryStatus | ''); setPage(1); }}
                className="h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                <option value="">Todos los estados</option>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <Button
                variant="outline" size="sm"
                onClick={() => qc.invalidateQueries({ queryKey: ['lead-deliveries'] })}
              >
                <RefreshCw className="size-4" /> Actualizar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Tipo</th>
                <th className="px-4 py-2">Lead</th>
                <th className="px-4 py-2">Intentos</th>
                <th className="px-4 py-2">Respuesta</th>
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
              )}
              {data?.data.length === 0 && !isLoading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-400">Aún no hay entregas registradas.</td></tr>
              )}
              {data?.data.map((d) => (
                <DeliveryRow
                  key={d.id}
                  delivery={d}
                  onView={() => setDetail(d)}
                  onRetry={() => retry.mutate(d.id)}
                  retrying={retry.isPending}
                />
              ))}
            </tbody>
          </table>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <PayloadDialog delivery={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function StatusBanner({ enabled, endpoint }: { enabled: boolean; endpoint: string }) {
  const ok = enabled && endpoint !== '';
  return (
    <div
      className={cnBanner(ok)}
    >
      {ok ? <CheckCircle2 className="size-5 shrink-0" /> : <XCircle className="size-5 shrink-0" />}
      <div className="text-sm">
        {ok ? (
          <>
            <span className="font-semibold">Integración activa.</span>{' '}
            Los leads se envían a <code className="rounded bg-black/5 px-1">{endpoint}</code>.
          </>
        ) : !endpoint ? (
          <>
            <span className="font-semibold">Sin configurar.</span>{' '}
            Define la URL del CRM en <Link to="/settings" className="underline">Ajustes → Integraciones</Link>.
          </>
        ) : (
          <>
            <span className="font-semibold">Desactivada.</span>{' '}
            Actívala en <Link to="/settings" className="underline">Ajustes → Integraciones</Link> para empezar a enviar leads.
          </>
        )}
      </div>
    </div>
  );
}

function cnBanner(ok: boolean): string {
  return [
    'flex items-center gap-3 rounded-xl border px-4 py-3',
    ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
       : 'border-amber-200 bg-amber-50 text-amber-800',
  ].join(' ');
}

function DeliveryRow({ delivery: d, onView, onRetry, retrying }: {
  delivery: LeadDelivery; onView: () => void; onRetry: () => void; retrying: boolean;
}) {
  const s = STATUS[d.status];
  const Icon = s.icon;
  return (
    <tr className="border-b border-neutral-100 hover:bg-neutral-50">
      <td className="px-4 py-3">
        <Badge variant={s.variant} className="gap-1"><Icon className="size-3" />{s.label}</Badge>
      </td>
      <td className="px-4 py-3 text-neutral-600">{TYPE_LABEL[d.lead_type] ?? d.lead_type}</td>
      <td className="px-4 py-3">
        {firstLeadField(d, 'email') ?? firstLeadField(d, 'name') ?? d.lead_id ?? '—'}
      </td>
      <td className="px-4 py-3 text-neutral-500">{d.attempts}</td>
      <td className="px-4 py-3 text-neutral-500">{d.response_code ?? '—'}</td>
      <td className="px-4 py-3 text-xs text-neutral-500">{new Date(d.created_at).toLocaleString('es')}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-2">
          {d.status === 'failed' && (
            <Button size="sm" variant="outline" onClick={onRetry} disabled={retrying}>
              <RotateCcw className="size-3.5" /> Reintentar
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onView}>Ver</Button>
        </div>
      </td>
    </tr>
  );
}

function firstLeadField(d: LeadDelivery, key: string): string | null {
  const lead = (d.payload?.lead ?? {}) as Record<string, unknown>;
  const v = lead[key];
  return typeof v === 'string' && v !== '' ? v : null;
}

function PayloadDialog({ delivery, onClose }: { delivery: LeadDelivery | null; onClose: () => void }) {
  return (
    <Dialog open={!!delivery} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Detalle de la entrega</DialogTitle>
          <DialogDescription>
            {delivery && `${TYPE_LABEL[delivery.lead_type] ?? delivery.lead_type} · ${new Date(delivery.created_at).toLocaleString('es')}`}
          </DialogDescription>
        </DialogHeader>
        {delivery && (
          <div className="min-w-0 space-y-4">
            {delivery.error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 break-words">
                {delivery.error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs text-neutral-600">
              <div className="min-w-0 break-words"><span className="text-neutral-400">Endpoint:</span> {delivery.endpoint ?? '—'}</div>
              <div><span className="text-neutral-400">HTTP:</span> {delivery.response_code ?? '—'}</div>
              <div><span className="text-neutral-400">Intentos:</span> {delivery.attempts}</div>
              <div><span className="text-neutral-400">Entregado:</span> {delivery.delivered_at ? new Date(delivery.delivered_at).toLocaleString('es') : '—'}</div>
            </div>
            <div className="min-w-0">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">Payload enviado</div>
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md border border-neutral-100 bg-neutral-50 p-3 text-xs text-neutral-800">
                {JSON.stringify(delivery.payload, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
